import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import api from '../api/apiClient';
import { setCurrentAnalysisId } from '../utils/appStateManagerAPI';

const useAnalysisStore = create(
  subscribeWithSelector((set, get) => ({
    currentAnalysis: null,
    analyses: [],
    isLoading: false,
    error: null,
    isWorkshopDataLoading: false,
    workshopDataError: null,

    loadAnalyses: async (user, isAuthenticated, page = 1, limit = 10) => {
      if (!isAuthenticated || !user) return;

      set({ isLoading: true, error: null });

      try {
        let url;
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString()
        });

        if (user.role === 'superadmin') {
          url = `/analyses?${params}`;
        } else if (user.role === 'admin' && user.companyId) {
          url = `/companies/${user.companyId}/analyses?${params}`;
        } else {
          url = `/analyses?${params}`;
        }

        const response = await api.get(url);

        if (response.success) {
          set({ analyses: response.data || [] });
          return {
            data: response.data || [],
            pagination: response.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
          };
        } else {
          set({ error: response.message || 'Failed to load analyses' });
          return { data: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
        }
      } catch (error) {
        console.error('Error loading analyses:', error);
        set({ error: 'Error loading analyses. Please try again later.' });

        if (process.env.NODE_ENV === 'development') {
          console.warn('Using mock analyses data for development');
          const mockData = [
            {
              id: 'mock-analysis-1',
              name: 'Risk Analysis - Main Project',
              description: 'Main cybersecurity risk analysis',
              status: 'in-progress',
              companyId: user.companyId,
              companyName: user.companyName,
              createdAt: '2025-01-15T08:30:00.000Z',
              updatedAt: '2025-03-10T14:22:00.000Z',
              classification: 'Confidentielle'
            },
            {
              id: 'mock-analysis-2',
              name: 'GDPR Compliance Analysis',
              description: 'Analysis for GDPR requirements',
              status: 'draft',
              companyId: user.companyId,
              companyName: user.companyName,
              createdAt: '2025-02-20T09:15:00.000Z',
              updatedAt: '2025-02-20T09:15:00.000Z',
              classification: 'Restreinte'
            }
          ];
          set({ analyses: mockData });
          return {
            data: mockData,
            pagination: { page: 1, limit: 10, total: mockData.length, pages: 1 }
          };
        }

        return { data: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
      } finally {
        set({ isLoading: false });
      }
    },

    loadAnalysis: async (analysisId) => {
      if (!analysisId || analysisId === 'null' || analysisId === 'undefined') {
        console.warn('[useAnalysisStore] Invalid analysis ID provided:', analysisId);
        return null;
      }

      const { currentAnalysis } = get();
      if (currentAnalysis && currentAnalysis.id === analysisId) {
        console.log('Analysis already loaded:', analysisId);
        return currentAnalysis;
      }

      set({ isLoading: true, error: null });

      try {
        const response = await api.get(`/analyses/${analysisId}`);

        if (response.success) {
          setCurrentAnalysisId(analysisId);
          set({ currentAnalysis: response.data });
          return response.data;
        } else {
          set({ error: response.message || 'Failed to load analysis' });
          return null;
        }
      } catch (error) {
        console.error('Error loading analysis:', error);
        set({ error: 'Error loading analysis. Please try again later.' });
        return null;
      } finally {
        set({ isLoading: false });
      }
    },

    createAnalysis: async (analysisData) => {
      set({ isLoading: true, error: null });

      try {
        const response = await api.post('/analyses', analysisData);

        if (response.success) {
          set(state => ({ analyses: [...state.analyses, response.data] }));
          return { success: true, data: response.data };
        } else {
          set({ error: response.message || 'Failed to create analysis' });
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error('Error creating analysis:', error);
        set({ error: 'Error creating analysis. Please try again later.' });
        return { success: false, message: error.message || 'Error creating analysis' };
      } finally {
        set({ isLoading: false });
      }
    },

    updateAnalysis: async (analysisId, analysisData) => {
      set({ isLoading: true, error: null });

      try {
        const response = await api.put(`/analyses/${analysisId}`, analysisData);

        if (response.success) {
          set(state => ({
            analyses: state.analyses.map(analysis =>
              analysis.id === analysisId ? response.data : analysis
            ),
            currentAnalysis: state.currentAnalysis?.id === analysisId
              ? response.data
              : state.currentAnalysis,
          }));
          return { success: true, data: response.data };
        } else {
          set({ error: response.message || 'Failed to update analysis' });
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error('Error updating analysis:', error);
        set({ error: 'Error updating analysis. Please try again later.' });
        return { success: false, message: error.message || 'Error updating analysis' };
      } finally {
        set({ isLoading: false });
      }
    },

    deleteAnalysis: async (analysisId) => {
      set({ isLoading: true, error: null });

      try {
        const response = await api.delete(`/analyses/${analysisId}`);

        if (response.success) {
          const { currentAnalysis } = get();
          const wasCurrent = currentAnalysis?.id === analysisId;

          set(state => ({
            analyses: state.analyses.filter(analysis => analysis.id !== analysisId),
            currentAnalysis: wasCurrent ? null : state.currentAnalysis,
          }));

          if (wasCurrent) {
            setCurrentAnalysisId(null);
          }

          return { success: true, wasCurrent };
        } else {
          set({ error: response.message || 'Failed to delete analysis' });
          return { success: false, message: response.message };
        }
      } catch (error) {
        console.error('Error deleting analysis:', error);
        set({ error: 'Error deleting analysis. Please try again later.' });
        return { success: false, message: error.message || 'Error deleting analysis' };
      } finally {
        set({ isLoading: false });
      }
    },

    updateRiskLevelsCount: async (count, onBroadcast) => {
      const { currentAnalysis } = get();
      if (!currentAnalysis?.id) return { success: false, message: 'No current analysis' };
      try {
        const response = await api.put(`/analyses/${currentAnalysis.id}`, { riskLevelsCount: count });
        if (response.success) {
          set({ currentAnalysis: { ...currentAnalysis, riskLevelsCount: count } });
          if (onBroadcast) onBroadcast('analysis-settings');
        }
        return response;
      } catch (error) {
        console.error('Error updating risk levels count:', error);
        return { success: false, message: error.message };
      }
    },

    updateRiskLevels: async (riskLevels) => {
      const { currentAnalysis } = get();
      if (!currentAnalysis?.id) return { success: false };
      try {
        const response = await api.put(`/analyses/${currentAnalysis.id}`, { riskLevels });
        if (response.success)
          set({ currentAnalysis: { ...currentAnalysis, riskLevels } });
        return response;
      } catch (error) {
        console.error('Error updating risk levels:', error);
        return { success: false, message: error.message };
      }
    },

    resetCoreState: () => {
      set({ currentAnalysis: null, analyses: [], isLoading: false, error: null, isWorkshopDataLoading: false, workshopDataError: null });
    },

    setCurrentAnalysis: (analysis) => {
      set({ currentAnalysis: analysis });
    },

    setAnalyses: (analyses) => {
      set({ analyses });
    },

    setIsLoading: (isLoading) => {
      set({ isLoading });
    },

    setError: (error) => {
      set({ error });
    },

    setIsWorkshopDataLoading: (isWorkshopDataLoading) => {
      set({ isWorkshopDataLoading });
    },

    setWorkshopDataError: (workshopDataError) => {
      set({ workshopDataError });
    },
  }))
);

export default useAnalysisStore;